import { inject, Injectable } from '@angular/core';
import { map, Observable, of, take, tap } from 'rxjs';

import { CookiesService } from './cookies.service';

import { ApiService } from '../../api/api.service';
import { UserStore } from '../../components/header/models/internal/user.store';

import { environment } from '../../../../environments/environment';

export const AITRUS_TOKEN = 'AITRUS_TOKEN';

/** Mesma janela que o otimiza-web usa. Encurtar só custa uma requisição a mais. */
const TTL_DIAS = 8 / 24;

/**
 * Abre a Aitrus autenticada, na tela pedida, com o CNPJ selecionado no pharma-dash.
 *
 * O token é buscado sob demanda, no clique — não no login. Assim o caminho de leitura
 * e o de renovação são o mesmo, e quem nunca abre a Aitrus nunca busca token.
 *
 * Falha na obtenção do token **não** abre aba: uma aba que cai na tela de login da
 * Aitrus não diz ao usuário o que deu errado nem o que fazer.
 */
@Injectable({ providedIn: 'root' })
export class AitrusSsoService {
    private readonly cookies = inject(CookiesService);
    private readonly api = inject(ApiService);
    private readonly userStore = inject(UserStore);

    /** Abre a Aitrus em nova aba. O observable falha se o token não vier — quem chama exibe o erro. */
    public abrir(redirect: string): Observable<void> {
        return this.buildSsoUrl(redirect).pipe(
            map(url => {
                window.open(url, '_blank');
            })
        );
    }

    public buildSsoUrl(redirect: string): Observable<string> {
        return this.token().pipe(
            map(token => {
                const url = new URL(environment.AITRUS_BASE_URL);
                url.searchParams.set('token', token);
                url.searchParams.set('redirect_uri', redirect);

                // Sem CNPJ selecionado o parâmetro fica de fora: um cnpj= vazio faria a
                // Aitrus abrir num contexto de loja indefinido em vez de pedir um.
                const cnpj = this.userStore.user().selectedCnpj;
                if (cnpj) {
                    url.searchParams.set('cnpj', cnpj);
                }

                return url.toString();
            })
        );
    }

    private token(): Observable<string> {
        const guardado = this.cookies.getItem(AITRUS_TOKEN);

        if (guardado) {
            return of(guardado);
        }

        // O prazo é do cookie: o navegador apaga o vencido, então "existe" já quer dizer
        // "válido". Guardar timestamp e comparar à mão seria reimplementar isso pior.
        return this.api.getAitrusToken().pipe(
            take(1),
            map(res => res.token),
            tap(token => this.cookies.setItem(AITRUS_TOKEN, token, TTL_DIAS))
        );
    }
}
